/** Sun-Screener ingredient decoder. */

import { announce } from '@theme/component';

const MIN_LOADING_MS = 2000;
const PROGRESS_INTERVAL_MS = 60;
const PROGRESS_CEILING = 99;
const VIEW_TOLERANCE_PX = 1;

function isParenthesised(value) {
  const text = String(value || '').trim();
  return text.startsWith('(') && text.endsWith(')');
}

/** Fills the `__name__` slots Liquid leaves in a copy template it hands over as data. */
function interpolate(template, values) {
  return String(template || '').replace(/__(\w+)__/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match
  );
}

/**
 * Resolve the API payload into the three display groups and the one scenario whose message the card shows.
 *
 * @param {Array} results - `results` from the decoder response.
 * @returns {{filters: Array, boosters: Array, inactive: Array, scenario: string, hasResults: boolean}}
 */
export function resolveResults(results) {
  const rows = Array.isArray(results) ? results : [];
  const filters = rows.filter((row) => row.matched && row.data?.uv_booster === 'UV');
  const boosters = rows.filter((row) => row.matched && row.data?.uv_booster === 'Booster');
  const inactive = rows.filter((row) => !row.matched && !isParenthesised(row.ingredient));

  const hasHybrid = filters.some((row) => row.data?.chemical_mineral === 'Hybrid');
  const hasMineral = filters.some((row) => row.data?.chemical_mineral === 'Mineral');
  const hasChemical = filters.some((row) => row.data?.chemical_mineral === 'Chemical');

  const mixed = filters.length > 0 && (hasHybrid || (hasMineral && hasChemical));
  const mineralOnly =
    filters.length > 0 && !mixed && filters.every((row) => row.data?.chemical_mineral === 'Mineral');
  const chemicalOnly =
    filters.length > 0 &&
    !mixed &&
    !mineralOnly &&
    filters.every((row) => row.data?.chemical_mineral === 'Chemical');
  const hasBoosters = boosters.length > 0;

  let scenario = 'not-sunscreen';
  if (mixed && !hasBoosters) scenario = 'mixed-no-booster';
  else if (mineralOnly && !hasBoosters) scenario = 'mineral-no-booster';
  else if (chemicalOnly && !hasBoosters) scenario = 'chemical-no-booster';
  else if (mixed && hasBoosters) scenario = 'mixed-with-booster';
  else if (mineralOnly && hasBoosters) scenario = 'mineral-with-booster';
  else if (chemicalOnly && hasBoosters) scenario = 'chemical-with-booster';

  return { filters, boosters, inactive, scenario, hasResults: filters.length > 0 };
}

/** Append one decoder rich-text node's content to `target` as DOM. */
function appendRichText(value, target) {
  if (value == null || value === '') return;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        appendRichTextNode(JSON.parse(value), target);
        return;
      } catch {
        // Not the JSON shape after all; fall through to the plain-text path.
      }
    }
    appendParagraphs(value, target);
    return;
  }

  if (typeof value === 'object') appendRichTextNode(value, target);
}

/** Plain text: blank lines separate paragraphs, single newlines are line breaks. */
function appendParagraphs(text, target) {
  String(text)
    .split(/\n{2,}/)
    .forEach((chunk) => {
      const paragraph = document.createElement('p');
      appendWithBreaks(chunk, paragraph);
      target.append(paragraph);
    });
}

function appendWithBreaks(text, target) {
  String(text)
    .split('\n')
    .forEach((line, index) => {
      if (index > 0) target.append(document.createElement('br'));
      if (line !== '') target.append(document.createTextNode(line));
    });
}

function appendRichTextNode(node, target) {
  if (!node || typeof node !== 'object') return;
  const children = Array.isArray(node.children) ? node.children : [];

  switch (node.type) {
    case 'root':
      children.forEach((child) => appendRichTextNode(child, target));
      return;

    case 'paragraph': {
      const paragraph = document.createElement('p');
      children.forEach((child) => appendRichTextNode(child, paragraph));
      target.append(paragraph);
      return;
    }

    case 'heading': {
      const level = Math.min(Math.max(Number(node.level) || 3, 3), 6);
      const heading = document.createElement(`h${level}`);
      children.forEach((child) => appendRichTextNode(child, heading));
      target.append(heading);
      return;
    }

    case 'list': {
      const list = document.createElement(node.listType === 'ordered' ? 'ol' : 'ul');
      children.forEach((child) => appendRichTextNode(child, list));
      target.append(list);
      return;
    }

    case 'list-item': {
      const item = document.createElement('li');
      children.forEach((child) => appendRichTextNode(child, item));
      target.append(item);
      return;
    }

    case 'link': {
      const link = document.createElement('a');
      const href = String(node.url || '');
      if (/^(https?:|mailto:)/i.test(href)) {
        link.href = href;
      }
      if (node.title) link.title = String(node.title);
      if (children.length) children.forEach((child) => appendRichTextNode(child, link));
      else link.append(document.createTextNode(String(node.text || href)));
      target.append(link);
      return;
    }

    case 'line-break':
      target.append(document.createElement('br'));
      return;

    case 'text': {
      let host = target;
      const wrap = (tag) => {
        const element = document.createElement(tag);
        host.append(element);
        host = element;
      };
      if (node.bold) wrap('strong');
      if (node.italic) wrap('em');
      if (node.underline) wrap('u');
      if (node.strikethrough) wrap('s');
      if (node.code) wrap('code');
      appendWithBreaks(node.value || '', host);
      return;
    }

    default:
      if (children.length) children.forEach((child) => appendRichTextNode(child, target));
      else if (node.value) target.append(document.createTextNode(String(node.value)));
  }
}

class SunscreenerApp extends HTMLElement {
  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;

    this.#form = this.querySelector('[data-sunscreener-form]');
    this.#input = this.querySelector('[data-sunscreener-input]');
    this.#submit = this.querySelector('[data-sunscreener-submit]');
    this.#results = this.querySelector('[data-sunscreener-results]');
    this.#percent = this.querySelector('[data-sunscreener-percent]');
    this.#status = this.querySelector('[data-sunscreener-status]');
    this.#dialog = this.ownerDocument.getElementById(this.dataset.dialogId || '');

    if (!this.#form || !this.#input || !this.#submit) return;

    this.#form.addEventListener('submit', this.#handleSubmit);
    this.#input.addEventListener('input', this.#queueInputSize);
    this.#input.addEventListener('focus', this.#clearEmpty);
    this.#input.addEventListener('input', this.#clearEmpty);
    this.#results.addEventListener('click', this.#handleResultsClick);
    this.#dialog?.addEventListener('close', this.#handleDialogClose);
    this.#resizeObserver = new ResizeObserver(this.#queueInputSize);
    this.#resizeObserver.observe(this);

    this.#submit.disabled = false;
    this.#queueInputSize();
  }

  disconnectedCallback() {
    this.#connected = false;
    this.#stopProgress();
    this.#abort?.abort();
    this.#abort = null;
    this.#form?.removeEventListener('submit', this.#handleSubmit);
    this.#input?.removeEventListener('input', this.#queueInputSize);
    this.#input?.removeEventListener('focus', this.#clearEmpty);
    this.#input?.removeEventListener('input', this.#clearEmpty);
    this.#results?.removeEventListener('click', this.#handleResultsClick);
    this.#dialog?.removeEventListener('close', this.#handleDialogClose);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#inputSizeFrame) cancelAnimationFrame(this.#inputSizeFrame);
    this.#inputSizeFrame = 0;
    window.DieuxFrame?.cancelMeasure(this.#revealKey);
  }

  #connected = false;
  #form = null;
  #input = null;
  #submit = null;
  #results = null;
  #percent = null;
  #status = null;
  #dialog = null;
  #abort = null;
  #resizeObserver = null;
  #progressTimer = 0;
  #progressStart = 0;
  #submitted = false;
  #inputSizeFrame = 0;
  #revealKey = Symbol('sunscreener-reveal');

  #queueInputSize = () => {
    if (this.#inputSizeFrame) cancelAnimationFrame(this.#inputSizeFrame);
    this.#inputSizeFrame = requestAnimationFrame(() => {
      this.#inputSizeFrame = 0;
      this.#syncInputSize();
    });
  };

  #syncInputSize() {
    if (!this.#input) return;

    if (this.#input.value === '') {
      const lineHeight = Number.parseFloat(getComputedStyle(this.#input).lineHeight);
      this.#setInputSize(lineHeight > 0 ? `${lineHeight}px` : 'auto');
      return;
    }

    this.#setInputSize('auto');
    this.#setInputSize(`${this.#input.scrollHeight}px`);
  }

  #setInputSize(value) {
    this.#input.style.setProperty('--sunscreener-input-size', value);
  }

  /** Clone one of the fragments Liquid rendered, with its copy already in place. */
  #clone(name) {
    const template = this.querySelector(`[data-sunscreener-template="${name}"]`);
    return template ? template.content.firstElementChild.cloneNode(true) : null;
  }

  #handleSubmit = (event) => {
    event.preventDefault();
    if (this.#submitted) this.#reset();
    else this.#analyse();
  };

  #clearEmpty = () => {
    if (this.dataset.state !== 'empty') return;
    this.dataset.state = 'idle';
    this.#syncRegions();
    this.#syncInvalid(false);
    this.#status.textContent = '';
  };

  /** Ties the empty-submit notice to the field while it shows. */
  #syncInvalid(invalid) {
    const notice = this.querySelector('[data-sunscreener-notice="empty"]');
    const noticeId = notice?.id;
    const ids = (this.#input.getAttribute('aria-describedby') || '').split(/\s+/).filter((id) => id && id !== noticeId);
    if (invalid && noticeId) ids.push(noticeId);

    if (ids.length) this.#input.setAttribute('aria-describedby', ids.join(' '));
    else this.#input.removeAttribute('aria-describedby');

    if (invalid) this.#input.setAttribute('aria-invalid', 'true');
    else this.#input.removeAttribute('aria-invalid');
  }

  #reset() {
    this.#abort?.abort();
    this.#abort = null;
    this.#stopProgress();
    this.#submitted = false;
    this.#input.value = '';
    this.#queueInputSize();
    this.#input.disabled = false;
    this.#results.replaceChildren();
    this.dataset.state = 'idle';
    delete this.dataset.result;
    this.#syncRegions();
    this.#status.textContent = '';
    this.#input.focus({ preventScroll: true });
    this.#queueReveal();
  }

  async #analyse() {
    const ingredients = this.#input.value.trim();
    if (ingredients === '') {
      this.#input.value = '';
      this.#queueInputSize();
      this.dataset.state = 'empty';
      this.#syncRegions();
      this.#syncInvalid(true);
      announce(
        this.#status,
        this.querySelector('[data-sunscreener-notice="empty"]')?.textContent.replace(/\s+/g, ' ').trim() || ''
      );
      return;
    }

    this.#abort?.abort();
    const controller = new AbortController();
    this.#abort = controller;

    this.dataset.state = 'loading';
    delete this.dataset.result;
    this.#syncRegions();
    this.#input.disabled = true;
    this.#submit.setAttribute('aria-busy', 'true');
    this.#status.textContent = this.dataset.statusLoading || '';
    this.#startProgress();
    this.#queueReveal();

    const started = Date.now();
    let payload = null;
    let failed = false;

    try {
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.dataset.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ingredients }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`Sunscreener request failed: ${response.status}`);
      payload = await response.json();
      if (!Array.isArray(payload?.results)) throw new Error('Sunscreener response invalid');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      failed = true;
    }

    const remaining = Math.max(0, MIN_LOADING_MS - (Date.now() - started));
    await new Promise((resolve) => {
      const timer = setTimeout(resolve, remaining);
      controller.signal.addEventListener('abort', () => {
        clearTimeout(timer);
        resolve();
      });
    });
    if (controller.signal.aborted || this.#abort !== controller) return;

    this.#abort = null;
    this.#stopProgress();
    this.#submit.removeAttribute('aria-busy');
    this.#submitted = true;

    if (failed) {
      this.#commit({ filters: [], boosters: [], inactive: [], scenario: 'error', hasResults: false });
      return;
    }

    this.#commit(resolveResults(payload?.results));
  }

  #commit({ filters, boosters, inactive, scenario, hasResults }) {
    this.#input.disabled = true;
    this.dataset.state = hasResults ? 'results' : 'error';
    this.dataset.result = scenario;

    this.#results.replaceChildren();
    if (hasResults) {
      this.#renderGroup('group-filters', filters);
      if (boosters.length) this.#renderGroup('group-boosters', boosters);
      if (inactive.length) this.#renderInactive(inactive);
    }

    this.#syncRegions();
    if (this.#form.contains(document.activeElement)) {
      window.DieuxAnchorScroll?.focusLanding(this.querySelector('[data-sunscreener-message]:not([hidden])'));
    }
    if (hasResults) {
      this.#status.textContent = interpolate(this.dataset.statusResults, { count: filters.length + boosters.length });
    } else {
      this.#status.textContent =
        (scenario === 'error' ? this.dataset.statusError : this.dataset.statusNoResults) || '';
    }
  }

  /** Scroll the section top under the sticky header when the card is not fully in view. */
  #queueReveal() {
    const frame = window.DieuxFrame;
    const section = this.closest('[id^="shopify-section-"]');
    if (!frame || !section || !this.#form) return;

    frame.measure(this.#revealKey, () => {
      if (!this.isConnected) return;

      const inset = frame.resolveCustomProperty(section, '--header-height');
      const card = this.#form.getBoundingClientRect();
      const inView =
        card.top >= inset - VIEW_TOLERANCE_PX && card.bottom <= window.innerHeight + VIEW_TOLERANCE_PX;
      if (inView) return;

      const top = Math.max(0, section.getBoundingClientRect().top + window.scrollY - inset);
      frame.write(() => {
        if (!this.isConnected) return;
        window.scrollTo({ top, behavior: frame.prefersReducedMotion() ? 'auto' : 'smooth' });
      });
    });
  }

  /**
   * One writer for every state-driven region, so no region can fall out of step
   * with the state attribute the stylesheet also reads.
   */
  #syncRegions() {
    const state = this.dataset.state;
    const result = this.dataset.result;
    const pending = state === 'idle' || state === 'loading' || state === 'empty';

    this.querySelectorAll('[data-sunscreener-message]').forEach((region) => {
      const key = region.dataset.sunscreenerMessage;
      region.toggleAttribute('hidden', pending ? key !== 'idle' : key !== result);
    });

    this.querySelectorAll('[data-sunscreener-notice]').forEach((region) => {
      region.toggleAttribute('hidden', region.dataset.sunscreenerNotice !== state);
    });

    this.querySelectorAll('[data-sunscreener-footer]').forEach((region) => {
      const key = region.dataset.sunscreenerFooter;
      const visible = (state === 'results' && key === 'results') || (state === 'error' && key === 'error');
      region.toggleAttribute('hidden', !visible);
    });

    const wanted = state === 'loading' ? 'submitting' : this.#submitted ? 'again' : 'submit';
    let wantedLabel = '';
    this.querySelectorAll('[data-sunscreener-label]').forEach((label) => {
      const key = label.dataset.sunscreenerLabel;
      label.toggleAttribute('hidden', key !== wanted);
      if (key === wanted) wantedLabel = label.textContent.trim();
    });
    this.#syncSubmitAnalytics(wantedLabel);
  }

  /** Keeps the submit control's declared GA4 button text on the label it currently shows. */
  #syncSubmitAnalytics(label) {
    let params = {};
    try {
      params = JSON.parse(this.#submit.dataset.ga4Params || '{}') || {};
    } catch {
      params = {};
    }
    if (params.custom_button_text === label) return;
    params.custom_button_text = label;
    this.#submit.dataset.ga4Params = JSON.stringify(params);
  }

  #renderGroup(templateName, rows) {
    const group = this.#clone(templateName);
    if (!group) return;

    const list = group.querySelector('[data-group-items]');
    rows.forEach((row, index) => {
      const pill = this.#clone('pill');
      if (!pill) return;
      pill.setAttribute('aria-controls', this.dataset.dialogId || '');
      pill.dataset.clickLinkName = row.matched_name || row.ingredient || '';
      pill.__sunscreenerItem = row;
      const name = String(row.matched_name || row.ingredient || '').trim();
      const cut = name.lastIndexOf(' ') + 1;
      pill.querySelector('[data-pill-name]').textContent = name.slice(0, cut);
      pill.querySelector('[data-pill-tail]').textContent = name.slice(cut);
      if (index < rows.length - 1) {
        const separator = document.createElement('span');
        separator.setAttribute('aria-hidden', 'true');
        separator.textContent = ',';
        pill.querySelector('.g-information-icon').after(separator);
      }
      if (index > 0) list.append(' ');
      list.append(pill);
    });

    this.#results.append(group);
  }

  #renderInactive(rows) {
    const group = this.#clone('group-inactive');
    if (!group) return;

    const trigger = group.querySelector('[data-inactive-trigger]');
    const panel = group.querySelector('[data-inactive-panel]');
    const panelId = `${this.id}-Inactive`;
    const triggerId = `${panelId}-Trigger`;

    trigger.id = triggerId;
    trigger.setAttribute('aria-controls', panelId);
    panel.id = panelId;
    panel.setAttribute('aria-labelledby', triggerId);
    group.querySelector('[data-inactive-list]').textContent = rows.map((row) => row.ingredient).join(', ');

    trigger.addEventListener('click', () => {
      const open = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', String(!open));
      panel.toggleAttribute('inert', open);
    });

    this.#results.append(group);
  }

  /** Fill the shared popup before `assets/dialog.js` opens it. */
  #handleResultsClick = (event) => {
    const pill = event.target.closest('[data-sunscreener-pill]');
    if (pill?.__sunscreenerItem) this.#fillDialog(pill.__sunscreenerItem);
  };

  #handleDialogClose = () => {
    this.#dialog?.querySelector('[data-sunscreener-dialog-rows]')?.replaceChildren();
  };

  #fillDialog(item) {
    const title = this.#dialog?.querySelector('[data-sunscreener-dialog-title]');
    const rows = this.#dialog?.querySelector('[data-sunscreener-dialog-rows]');
    if (!title || !rows) return;

    title.textContent = item.matched_name || item.ingredient || '';
    rows.replaceChildren();

    const data = item.data || {};
    const isBooster = data.uv_booster === 'Booster';

    this.#appendRow('row-type', (value, copy) => {
      const kind = data.chemical_mineral;
      value.textContent = isBooster
        ? copy.valueBooster
        : interpolate(copy.valueFilter, { kind: kind === 'Hybrid' ? copy.kindHybrid : String(kind || '') });
    });

    if (Array.isArray(item.other_names) && item.other_names.length) {
      this.#appendRow('row-aka', (value) => {
        value.textContent = item.other_names.join(', ');
      });
    }

    if (isBooster) {
      if (data.structurally_similar_to) {
        this.#appendRow('row-similar', (value) => {
          value.textContent = data.structurally_similar_to;
        });
      }
      if (data.booster_fyi) {
        this.#appendRow('row-fyi', (value) => appendRichText(data.booster_fyi, value));
      }
      if (data.details) {
        this.#appendRow('row-about-booster', (value) => appendRichText(data.details, value));
      }
      return;
    }

    this.#appendRow('row-fyi', (value, copy) => {
      const limit = (amount) => (amount ? interpolate(copy.limit, { value: amount }) : '');
      const us = data.approved_in_us ? copy.usApproved : copy.usNotApproved;
      appendRichText(
        [
          interpolate(us, { limit: limit(data.max_threshold_us_num) }),
          interpolate(copy.euApproved, { limit: limit(data.max_threshold_eu_num) }),
        ].join('\n\n'),
        value
      );
    });

    if (data.details) {
      this.#appendRow('row-about-filter', (value) => appendRichText(data.details, value));
    }
  }

  /**
   * Each row template arrives from Liquid with its label rendered and any copy
   * template it needs on its own dataset, so the fill callback only writes the value.
   */
  #appendRow(templateName, fill) {
    const template = this.querySelector(`[data-sunscreener-template="${templateName}"]`);
    if (!template) return;
    const row = template.content.firstElementChild.cloneNode(true);
    fill(row.querySelector('[data-row-value]'), template.dataset);
    this.#dialog.querySelector('[data-sunscreener-dialog-rows]').append(row);
  }

  #startProgress() {
    this.#progressStart = Date.now();
    this.#setProgress(0);
    this.#progressTimer = window.setInterval(() => {
      const elapsed = Date.now() - this.#progressStart;
      const ratio = 1 - Math.pow(1 - Math.min(elapsed / MIN_LOADING_MS, 1), 3);
      this.#setProgress(Math.min(Math.round(ratio * PROGRESS_CEILING), PROGRESS_CEILING));
    }, PROGRESS_INTERVAL_MS);
  }

  #stopProgress() {
    if (this.#progressTimer) window.clearInterval(this.#progressTimer);
    this.#progressTimer = 0;
    this.#setProgress(100);
  }

  #setProgress(value) {
    this.style.setProperty('--sunscreener-progress', String(value));
    if (this.#percent) this.#percent.textContent = `${value}%`;
  }
}

if (!customElements.get('sunscreener-app')) {
  customElements.define('sunscreener-app', SunscreenerApp);
}
